import { z } from 'zod';
import { PROTOCOL_DEFAULT_CLOSE_TEXT } from './protocol.js';

/**
 * The global rules and the guardrails of Réglages (§8.5, §11.3).
 *
 * Two lists, one screen, and they are NOT the same object - which is the whole
 * shape of this file:
 *
 * - a **global rule** is a DIRECTION served to the interviewer, in a list whose
 *   ORDER is its priority (§10.3 serves them « par priorité », and the rendered
 *   block ends on « en cas de conflit, la règle la plus haute prime »). Order is
 *   content, so the list is edited and saved WHOLE;
 * - a **guardrail** is what the scribe must detect, for how many consecutive
 *   turns, and what the engine then does. It has NO order - §8.5 says « un
 *   garde-fou déclenché l'emporte » and nothing about which one wins first - so
 *   a guardrail is edited and saved ON ITS OWN.
 *
 * ## Neither is versioned
 *
 * Same régime as the two reference lists of §11.5: these are settings the team
 * edits in place, they apply « à toutes les sessions », and there is no history
 * to walk back. A session freezes what it plays (§5.2), so the rules and
 * guardrails a turn was judged against are read from the base AT THAT TURN and
 * recorded in the turn itself (§8.7) - the lot that writes turns is what owns
 * that record, not this file.
 *
 * ## The seed, and what happens to it after
 *
 * §11.3 seeds TWO rules and TWO guardrails at the first boot, and then « la base
 * fait foi » : the team keeps, rewrites, reorders or removes them like anything
 * else. The texts live in `packages/backend/src/rules/seed.ts`, beside the
 * write that plays them once.
 */

/* ------------------------------------------------------ bounds of input */

/**
 * The ceilings of what an operator types, and they are bounds of INPUT rather
 * than of meaning (H13): a rule refused at 1001 characters says nothing about
 * what a good rule is.
 *
 * One thousand for the three long fields, which is the order of magnitude of a
 * cran's direction next door: these texts reach a model as-is, and the cost of
 * a long one is paid on every turn of every session.
 */
export const GLOBAL_RULE_TEXT_MAX = 1000;
export const GUARDRAIL_DETECTION_MAX = 1000;
export const GUARDRAIL_DIRECTION_MAX = 1000;

/**
 * How many consecutive turns a guardrail can be asked to wait for (§8.5).
 *
 * A bound of PLAUSIBILITY, like the block ceiling below: §8.5 gives N no
 * maximum, but a step carries a turn ceiling of its own (§7.2), so a guardrail
 * waiting twenty turns in a row is a guardrail that can never fire - and a
 * typed `300` would look like a working setting.
 */
export const GUARDRAIL_CONSECUTIVE_TURNS_MAX = 20;

/** « pendant N tours consécutifs, un nombre, défaut 1 » (§8.5). */
export const GUARDRAIL_CONSECUTIVE_TURNS_DEFAULT = 1;

/* ------------------------------------------------------- the global rules */

/**
 * One global rule (§8.5): a direction served to the interviewer, at a priority.
 *
 * No name beside the text, and that absence is deliberate: §10.3 serves the
 * rules to the interviewer as a numbered list of directions, so a name would be
 * a second field with nowhere to go - it would either leak into the prompt as
 * noise, or exist only for the screen that edits it.
 */
export const globalRuleSchema = z
  .object({
    id: z.string().min(1),
    text: z.string().min(1).max(GLOBAL_RULE_TEXT_MAX),
    /** Dense and zero-based, ascending: the LOWEST number is the highest priority. */
    priority: z.number().int().nonnegative(),
  })
  .strict();
export type GlobalRule = z.infer<typeof globalRuleSchema>;

/** The whole list, by priority. Short by construction - it is a prompt's worth of rules. */
export const globalRuleListSchema = z.object({ rules: z.array(globalRuleSchema) }).strict();
export type GlobalRuleList = z.infer<typeof globalRuleListSchema>;

/**
 * A ceiling on the BLOCK, for the reason `INVOLVEMENT_LEVELS_BLOCK_MAX` gives:
 * one authenticated request must not be able to ask the single SQLite
 * connection for an unbounded transaction. Fifty rules is already past
 * anything a prompt can carry.
 */
export const GLOBAL_RULES_BLOCK_MAX = 50;

/**
 * One row of the block: the text, plus the id when the base holds it.
 *
 * `id` absent means « create this one » - absent rather than null, because that
 * is what a row the operator has just typed actually carries.
 */
export const globalRuleBlockRowSchema = z
  .object({
    id: z.string().min(1).optional(),
    text: z.string().trim().min(1).max(GLOBAL_RULE_TEXT_MAX),
  })
  .strict();
export type GlobalRuleBlockRow = z.infer<typeof globalRuleBlockRowSchema>;

/**
 * The WHOLE list, as the screen holds it, for one transaction.
 *
 * THE ORDER OF THE ARRAY IS THE ORDER OF THE PRIORITIES - there is no
 * `priority` field to disagree with it, exactly as the involvement scale of
 * §11.5 carries no `position`.
 *
 * ## A row the block does not name IS DELETED, and that is the one difference
 *
 * The scale keeps what its block leaves out, because a cran has a deletion
 * route of its own, with a confirmation and a count. A rule has none: §11.3
 * gives it « création, édition, suppression, et un ordre qu'on déplace », all
 * four of them gestures on the same ordered list, and a `DELETE /{id}` beside
 * this block would be a second way to say what the block already says. So the
 * block is the list, and leaving a row out is how one goes.
 *
 * What that costs is the concurrent case §11.5 already assumes for its two
 * reference lists: a rule a colleague adds between this screen's read and its
 * save is not named by the block, so it goes with it. These lists carry no
 * version token, and this is the same conflict, named rather than solved.
 */
export const globalRuleBlockSchema = z
  .object({ rules: z.array(globalRuleBlockRowSchema).max(GLOBAL_RULES_BLOCK_MAX) })
  .strict();
export type GlobalRuleBlock = z.infer<typeof globalRuleBlockSchema>;

/* ---------------------------------------------------------- the guardrails */

/**
 * The five actions of §8.5, as a closed list - the order the select shows them in.
 * « Conclure par l'adieu » (issue #163) is THIRD, right after the two closings it
 * is a gentler form of: it goes to the farewell step, or closes the protocol.
 */
export const guardrailActionKindSchema = z.enum([
  'close-experience',
  'close-protocol',
  'conclude-with-farewell',
  'inject-direction',
  'flag-only',
]);
export type GuardrailActionKind = z.infer<typeof guardrailActionKindSchema>;

/**
 * What the engine does when a guardrail fires (§8.5), as a discriminated union.
 *
 * A union rather than a `kind` plus a nullable text, because exactly one of the
 * five carries anything: `inject-direction` has the sentence handed to the
 * interviewer, and the other four have nothing to say. Held as a kind and an
 * optional text, « injecter une direction » with no direction would be a legal
 * row - and the turn that fired it would serve an empty instruction.
 *
 * `close-experience` carries NO text either, and that is §8.5 in as many words:
 * it closes « avec le texte de clôture par défaut (§7.2) », which is
 * `PROTOCOL_DEFAULT_CLOSE_TEXT` and is not editable here. A guardrail is global
 * and a closing text is the words of one experience; making it editable per
 * guardrail would put the same sentence in two places that can disagree.
 */
export const guardrailActionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('close-experience') }).strict(),
  z.object({ kind: z.literal('close-protocol') }).strict(),
  /**
   * « Conclure par l'adieu » (issue #163, §8.5, H78): go to the FIRST farewell
   * step of the protocol played, or close the protocol when it has none. It
   * carries nothing - the target is read off the frozen protocol at the turn
   * (`firstFarewellStep`), since a guardrail is global and a step id is not.
   */
  z
    .object({ kind: z.literal('conclude-with-farewell') })
    .strict(),
  z
    .object({
      kind: z.literal('inject-direction'),
      /** « injecter une direction à l'interviewer » - the sentence it is handed. */
      text: z.string().trim().min(1).max(GUARDRAIL_DIRECTION_MAX),
    })
    .strict(),
  z.object({ kind: z.literal('flag-only') }).strict(),
]);
export type GuardrailAction = z.infer<typeof guardrailActionSchema>;

/** The default closing text a `close-experience` guardrail uses (§7.2, §8.5). */
export const GUARDRAIL_CLOSE_TEXT = PROTOCOL_DEFAULT_CLOSE_TEXT;

/** One guardrail (§8.5): what to detect, for how long, and what to do about it. */
export const guardrailSchema = z
  .object({
    id: z.string().min(1),
    /** « ce que le scribe doit détecter sur le tour », in free text. */
    detection: z.string().min(1).max(GUARDRAIL_DETECTION_MAX),
    /** « pendant N tours consécutifs », the engine keeping the run itself. */
    consecutiveTurns: z.number().int().min(1).max(GUARDRAIL_CONSECUTIVE_TURNS_MAX),
    action: guardrailActionSchema,
  })
  .strict();
export type Guardrail = z.infer<typeof guardrailSchema>;

/** The whole list, oldest first. A guardrail has no priority (§8.5). */
export const guardrailListSchema = z.object({ guardrails: z.array(guardrailSchema) }).strict();
export type GuardrailList = z.infer<typeof guardrailListSchema>;

/**
 * Creating a guardrail, or rewriting one. The same three fields either way, and
 * no block: a guardrail is saved ON ITS OWN.
 *
 * `consecutiveTurns` defaults to 1 (§8.5) at the boundary rather than in the
 * screen, so a caller that never heard of the field still writes the value §8.5
 * names - a `curl` included.
 */
export const guardrailWriteSchema = z
  .object({
    detection: z.string().trim().min(1).max(GUARDRAIL_DETECTION_MAX),
    consecutiveTurns: z
      .number()
      .int()
      .min(1)
      .max(GUARDRAIL_CONSECUTIVE_TURNS_MAX)
      .default(GUARDRAIL_CONSECUTIVE_TURNS_DEFAULT),
    action: guardrailActionSchema,
  })
  .strict();
export type GuardrailWrite = z.infer<typeof guardrailWriteSchema>;

/* ------------------------------------------------------------ the refusals */

/**
 * What these two lists refuse with a machine code, as ONE closed catalogue -
 * and it holds exactly ONE code, which is the doctrine of §11.5's catalogue
 * taken to its end: « a shared catalogue would offer six codes where two
 * exist ».
 *
 * The two refusals one expects beside it are deliberately NOT here:
 *
 * - a block naming the same rule twice is a MALFORMED block, so it is a 400,
 *   like the block of the involvement scale;
 * - a `PUT` or a `DELETE` on a guardrail the base does not hold is a 404, like
 *   every other addressed row of the administration.
 *
 * `rule-unknown` is neither. The block of §11.3 is the only way a rule ever
 * leaves the list - there is no deletion route - so an id the base does not
 * hold is an id that WAS there when this screen read it, and what happened
 * since is a colleague's save. That is a state of the base and not a wrong
 * address, which is what a 409 says; and it is the one outcome the screen has
 * something to do about - come back to the base and show what it now holds.
 */
export const rulesErrorCodeSchema = z.enum(['rule-unknown']);
export type RulesErrorCode = z.infer<typeof rulesErrorCodeSchema>;

/** `id` names the row the block claimed, so the screen can say which one went. */
export const rulesErrorSchema = z
  .object({
    error: z.string().min(1),
    code: rulesErrorCodeSchema,
    id: z.string().optional(),
  })
  .strict();
export type RulesError = z.infer<typeof rulesErrorSchema>;
